import { addDays, format, startOfDay } from "date-fns";
import { usePhoneInput } from "mt-components/PhoneNumberInput";
import { useCallback, useMemo, useState } from "react";
import { EditWaitingListMutation, Product } from "../../../graphql/operations";
import useWaitingList from "../../Templates/WaitingList/useWaitingList";
import MultiCardModal, { MultiCardModalProps } from "../MultiCardModal";
import DateRangeSearchCard from "../MultiCardModal/components/DateRangeSearchCard";
import GuestSearchCard from "../MultiCardModal/components/GuestSearchCard";
import ContactCard from "./ContactCard";
import ProductCard, { ProductCardProps } from "./ProductCard";

export type WaitingListModalProps = {
  productId?: Product["id"];
  email?: string;
  phoneNumber?: string;
  timeZone?: string;
  onAddWaitingList?: (
    result: EditWaitingListMutation["editWaitingList"]
  ) => void;
} & Pick<MultiCardModalProps, "open" | "onClose"> &
  Pick<ProductCardProps, "productOptions">;

export default function WaitingListModal({
  open,
  onClose,
  productOptions,
  onAddWaitingList,
  ...props
}: WaitingListModalProps) {
  const [email, setEmail] = useState(props.email ?? "");
  const [phoneNumber, setPhoneNumber] = useState(props.phoneNumber ?? "");

  const phoneInput = usePhoneInput({ phoneNumber, setPhoneNumber });

  const [timeZone, setTimeZone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  const [productId, setProductId] = useState(
    props.productId ?? productOptions[0].id
  );
  const emailDisabled = useMemo(() => props.email !== undefined, [props.email]);
  const productDisabled = useMemo(
    () => props.productId !== undefined,
    [props.productId]
  );

  const [fromDate, setFromDate] = useState<Date>(
    startOfDay(addDays(new Date(), 7))
  );
  const [untilDate, setUntilDate] = useState<Date>(
    startOfDay(addDays(new Date(), 14))
  );

  const [groupSize, setGroupSize] = useState<number>(2);

  const { addWaitingList } = useWaitingList();

  const addWaitingListWithVariables = useCallback(async () => {
    const { data } = await addWaitingList(
      {
        email,
        phoneNumber: (phoneInput.localNumber && phoneNumber) || undefined,
        fromDate: format(fromDate, "yyyy-MM-dd"),
        untilDate: format(untilDate ?? fromDate, "yyyy-MM-dd"),
        groupSize,
        productId,
        active: true,
      },
      props.timeZone ?? timeZone,
      true
    );
    onAddWaitingList?.(data.editWaitingList);
  }, [
    email,
    fromDate,
    untilDate,
    groupSize,
    productId,
    timeZone,
    phoneInput,
    phoneNumber,
    addWaitingList,
    onAddWaitingList,
    props.timeZone,
  ]);

  const errorMessage = useMemo(() => {
    if (!(email || phoneInput.localNumber))
      return "Please enter an email address or phone number";
    if (
      email &&
      !email.match(/^([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/)
    )
      return "Please check if the email address is valid";
    if (phoneInput.error) return phoneInput.error;
  }, [email, phoneInput]);

  const cards = [
    productDisabled ? (
      []
    ) : (
      <ProductCard
        key="productcard"
        shortTitle="Which"
        title="Which guided tour?"
        productId={productId}
        setProductId={setProductId}
        productOptions={productOptions}
        disabled={productDisabled}
      />
    ),
    <DateRangeSearchCard
      key={"daterangecard"}
      shortTitle="When"
      title="When's your trip?"
      from={fromDate}
      setFrom={setFromDate}
      until={untilDate}
      setUntil={setUntilDate}
      maxRange={90}
    />,
    <GuestSearchCard
      key={"guestcard"}
      shortTitle="Who"
      title="Who's coming?"
      numGuests={groupSize}
      setNumGuests={setGroupSize}
    />,
    emailDisabled ? (
      []
    ) : (
      <ContactCard
        key={"contactcard"}
        shortTitle="Contact"
        title="How can we reach you?"
        email={email}
        setEmail={setEmail}
        timeZone={timeZone}
        setTimeZone={setTimeZone}
        phoneInput={phoneInput}
        disabled={emailDisabled}
      />
    ),
  ].flat();

  return (
    <MultiCardModal
      title="Sign up"
      open={open}
      onClose={onClose}
      cards={cards}
      onSubmit={() => addWaitingListWithVariables()}
      submitLabel="Join waiting list"
      errorMessage={errorMessage}
      successMessage="Signed up!"
    />
  );
}
